import { type FC, useEffect, useMemo, useState } from "react";
import { Icon } from "@root/shared/components/Icon";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import { colors, fontSize } from "@root/shared/styles/sprinkles.css";
import { TextButton } from "@root/shared/components/TextButton";
import { sendAppWebhook } from "@root/userFrontend/utils/sendAppWebhook";
import { useInView } from "react-intersection-observer";
import * as styles from "./styles.css";
import { OtherWorksLink } from "../OtherWorksLink";

type Props = {
  otherShops: {
    path: string;
    text: string;
    shopId: string;
  }[];
  detailWorkCode: string;
  showOtherWorksLink?: boolean;
};

type LinkProps = {
  path: string;
  text: string;
  shopId: string;
  detailWorkCode: string;
  isOpen: boolean;
  handleOnClickLink: (workCode: string, shopId: string) => void;
};

const DEFAULT_SHOW_SHOP_LENGTH = 5;
const GA_OTHER_SHOPS_ACTION = "job_detail__other_shops";
const APP_OTHER_SHOPS_ACTION = "tap_job_detail__other_shops";
const APP_OTHER_SHOPS_SHOW_ACTION = "imp_job_detail__other_shops";

export const WorkDetailOtherShops: FC<Props> = ({
  otherShops,
  detailWorkCode,
  showOtherWorksLink,
}) => {
  const [isOpen, setIsOpen] = useState(DEFAULT_SHOW_SHOP_LENGTH >= otherShops.length);
  const handleOnClickOpenButton = () => {
    setIsOpen(true);
  };

  const handleOnClickShopLink = (workCode: string, shopId: string) => {
    const appEventCommonMessage = {
      work_code: workCode,
      sid: shopId,
    };
    sendAppWebhook({
      iosEventMessage: {
        ...appEventCommonMessage,
        message: "firebase",
        contentID: APP_OTHER_SHOPS_ACTION,
      },
      androidEventName: "firebase",
      androidEventMessage: JSON.stringify({
        ...appEventCommonMessage,
        content_id: APP_OTHER_SHOPS_ACTION,
      }),
    });
  };

  return (
    <div className={styles.outer}>
      <GuidanceBox themeColor="pink">
        <div className={styles.guidanceInner}>
          <Icon name="light" fontSize={fontSize["fs-16"]} color={colors.Biyo} />
          勤務地が希望に合わなくても、応募した後に相談できることがあります。
        </div>
      </GuidanceBox>
      <p className={styles.title}>この求人の別店舗</p>
      <div className={styles.shopsOuter}>
        {otherShops.map(({ path, text, shopId }) => (
          <ShopLink
            key={text}
            path={path}
            text={text}
            shopId={shopId}
            detailWorkCode={detailWorkCode}
            isOpen={isOpen}
            handleOnClickLink={handleOnClickShopLink}
          />
        ))}
        {showOtherWorksLink && isOpen && <OtherWorksLink className={styles.otherWorks} />}
      </div>
      {!isOpen && (
        <div className={styles.textButtonOuter}>
          <TextButton size="large" leftIconName="add-more" isBold onClick={handleOnClickOpenButton}>
            続きを見る
          </TextButton>
        </div>
      )}
    </div>
  );
};

const ShopLink: FC<LinkProps> = ({
  text,
  path,
  detailWorkCode,
  shopId,
  isOpen,
  handleOnClickLink,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const appEventCommonMessage = useMemo(
    () => ({
      work_code: detailWorkCode,
      sid: shopId,
    }),
    [detailWorkCode, shopId],
  );

  useEffect(() => {
    if (inView) {
      sendAppWebhook({
        iosEventMessage: {
          ...appEventCommonMessage,
          message: "firebase",
          contentID: APP_OTHER_SHOPS_SHOW_ACTION,
        },
        androidEventName: "firebase",
        androidEventMessage: JSON.stringify({
          ...appEventCommonMessage,
          content_id: APP_OTHER_SHOPS_SHOW_ACTION,
        }),
      });
    }
  }, [appEventCommonMessage, inView]);

  return (
    <p key={text} className={styles.shopLinkOuter({ isOpen })}>
      <a
        href={path}
        className={styles.shopLink}
        data-ga-action={GA_OTHER_SHOPS_ACTION}
        data-ga-label={`${detailWorkCode}_${shopId}`}
        onClick={() => handleOnClickLink(detailWorkCode, shopId)}
        ref={ref}
      >
        <span className={styles.shopLinkInner}>{text}</span>
        <span className={styles.iconOuter}>
          <Icon name="arrow-light-right" />
        </span>
      </a>
    </p>
  );
};
